import { useMutation } from '@tanstack/react-query'

import { TUser } from '~/features/user/models'
import { queryKeys } from '~/features/user/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'

type TValidateUserPayload = {
  email: string
  cognitoId: string
}

/**
 * Hook used to validate if the user exists in the database and if the cognitoId matches the one in the database.
 * This is used to prevent users to access the application with other means than with what they registered.
 * Example: if a user registered with Google SSO, they should not be able to access the application with email and password,
 * even if the email is the same, because the cognitoId will be different.
 *
 * @returns useMutation result with a mutation function that accepts an object with email and cognitoId
 * and returns the user if the validation is successful, or an error if the validation fails.
 * The error can be either a string message or an object with statusCode and message,
 * depending on the type of error that occurred during the validation process.
 * The validation process can fail if the user does not exist in the database,
 * if the cognitoId does not match the one in the database, or if there is an issue with the database connection.
 *
 * @example
 * const { mutate: validateUser } = useValidateUser()
 *
 * validateUser({ email: 'john@description.com', cognitoId: 'abc-123' }, {
 *   onSuccess: (user) => {
 *     // user is valid, proceed with the application logic
 *   },
 *   onError: (error) => {
 *     // handle the error, show a message to the user, etc.
 *   },
 * })
 * @returns TUser
 */
const useValidateUser = () => {
  return useMutation({
    retry: false,
    mutationKey: queryKeys.apiUsersValidate(),
    mutationFn: (payload: TValidateUserPayload) =>
      HttpService.post<TUser | { statusCode: string; message: string }>(
        {
          path: EServerPath.apiUsersValidate,
          payload,
        },
        EHttpService.internal,
      ),
  })
}

export default useValidateUser
